<template>
  <DsBottomSheet
    ref="paymentSheet"
    :model-value="isOpen"
    :breakpoints="sheetStops"
    :initial-breakpoint="sheetStop"
    :backdrop-breakpoint="0"
    :backdrop-dismiss="canDismiss"
    @update:modelValue="handleDismiss"
  >
    <!-- Height is MEASURED, not computed. Ionic sizes this box to the sheet's
         FULL travel (measured 834px tall in an 844px viewport) and moves only
         its top, so `h-full` puts everything anchored with `mt-auto`
         off-screen. Deriving the visible height arithmetically instead
         (breakpoint x 100dvh - 8px) was closer but still wrong: at breakpoint
         0.75 on a 402x874pt iPhone it predicted 647.5pt against an actual
         601.3pt of visible sheet, and the 46pt of overshoot swallowed the whole
         foot padding and clipped 17pt off the Close button. The sheet's own box
         is not the viewport's share of the breakpoint — Ionic insets it, by an
         amount that is not a constant and is not ours to predict.
         So: ask the element where it is. `sheetVisibleHeight` is
         `viewport bottom - this box's top`, which is exact at every breakpoint
         and needs no knowledge of Ionic's internals or the safe-area insets.
         The Tailwind classes remain as the pre-measurement fallback for the
         first frame; `100vh` is that fallback's own floor, because iOS 13-15.3
         and Chrome/WebView 80-107 (our browserslist) cannot parse `dvh` and
         would drop a dvh-only height, leaving this box auto-sized. -->
    <div
      ref="sheetBox"
      class="flex flex-col px-1 pt-1"
      :class="
        usesExplicitHeight
          ? 'h-[calc(var(--sheet-bp)*100vh-8px)] supports-[height:1dvh]:h-[calc(var(--sheet-bp)*100dvh-8px)]'
          : ''
      "
      :style="sheetBoxStyle"
    >
      <!-- Method selection + slide to pay (also hosts the empty state) -->
      <PaymentMethodStep
        v-if="currentStep === 'method'"
        ref="methodStep"
        :linked-accounts="offeredBankAccounts"
        :bank-rail-allowed="bankRailAllowed"
        :preselected-id="selectedMethod?.id || request?.preselectedMethod"
        :loading="accountsLoading"
        :amount="request?.amount || 0"
        :category="request?.category || ''"
        :description="request?.description || ''"
        @select="handleMethodSelect"
        @confirm="handleConfirm"
        @checkout="handleCheckoutTile"
        @close="closeSheet"
      />

      <!-- PIN verification (gates large bank transfers) -->
      <Pin
        v-else-if="currentStep === 'pin'"
        ref="pinOverlay"
        :open="true"
        :mode="pinMode"
        :locked-until="pinLockedUntil"
        variant="local-transfer-egiro"
        :allow-backdrop-close="true"
        @verify="handlePinVerify"
        @cancel="goBackToMethod"
        @forgot="handleForgotPin"
        @lock-expired="pinMode = 'verify'"
      />

      <!-- Processing -->
      <PaymentProcessingStep v-else-if="currentStep === 'processing'" />

      <!-- Success / pending / failure -->
      <PaymentSuccessStep
        v-else-if="currentStep === 'success'"
        :can-scroll="usesExplicitHeight"
        :status="successStatus"
        :amount="result?.totalPaid || result?.amount || 0"
        :description="result?.description || request?.description || ''"
        :transaction-id="result?.transactionId"
        :reference-code="result?.referenceCode"
        :paid-from="result?.paymentMethod?.label"
        :error-message="result?.message"
        @done="closeSheet"
      />
    </div>
  </DsBottomSheet>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { BottomSheet as DsBottomSheet } from '@team-hogc/hogc-one-design-system';
import PaymentMethodStep from './PaymentMethodStep.vue';
import PaymentProcessingStep from './PaymentProcessingStep.vue';
import PaymentSuccessStep from './PaymentSuccessStep.vue';
import Pin from '@/components/MyGiving/Common/Pin.vue';
import { PaymentStatus } from '@/constants/Payment/payment-methods';

/**
 * Foot of the sheet. `--ion-safe-area-bottom` first, matching MasterLayout, with
 * the raw env() as the fallback — `viewport-fit=cover` is set in index.html so
 * it resolves on iOS (34pt on home-indicator devices). On Android it is 0 today
 * because styles.xml opts out of edge-to-edge enforcement, which keeps the
 * WebView inset by the nav bar; that opt-out is deprecated, so reading the inset
 * here is what keeps this correct when it goes away. The floor carries the
 * no-inset case on its own — Android reports 0 today, so it cannot rely on the
 * inset for its breathing room.
 */
const SHEET_FOOT_PADDING =
  'max(3.25rem, calc(var(--ion-safe-area-bottom, env(safe-area-inset-bottom, 0px)) + 1.25rem))';

/**
 * How long to keep re-measuring after a breakpoint change. Ionic's sheet
 * transition is 300ms; the margin covers a frame or two of settling. Cheap —
 * it is a rect read per frame, for a third of a second, only while the sheet
 * is moving.
 */
const SHEET_TRANSITION_TRACK_MS = 400;

/**
 * Steps whose height is derived from their own content rather than declared.
 *
 * These are the short ones. The result step (success / pending / failed) was
 * pinned at 0.75 and processing at 0.45, which on the failed screen left a
 * third of the display as blank sheet between the message and the Close
 * button. A declared fraction cannot know how much content a step has, and
 * these two vary: failed has no receipt block, success and pending do.
 *
 * The method and PIN steps are NOT here on purpose. Their height is a layout
 * decision, not a content one — the method step is deliberately tall to seat
 * the accounts list above a slide-to-pay footer.
 */
const AUTO_SIZED_STEPS = ['processing', 'success'];

/**
 * Bounds on a content-derived stop. The floor stops a one-line step rendering
 * as a sliver that reads as a broken sheet; the ceiling is the tallest stop the
 * sheet has ever used, and hitting it is what switches the box back to a fixed
 * height so its content scrolls instead of overflowing.
 */
const MIN_SHEET_STOP = 0.28;
const MAX_SHEET_STOP = 0.95;

/** Declared stop for the steps that are not content-sized. */
const TALL_METHOD_STOP = 0.95;
const SHORT_METHOD_STOP = 0.66;

const clampStop = value => Math.min(MAX_SHEET_STOP, Math.max(MIN_SHEET_STOP, value));

/**
 * How far a pan must travel before we call its direction.
 *
 * Ionic's sheet gesture captures a pan once it has moved 10px
 * (`createPanRecognizer` squares its threshold and compares squared distance),
 * so deciding at 6px means we get to act BEFORE it takes the pan. Below that a
 * pan has no meaningful direction yet.
 */
const DRAG_DIRECTION_THRESHOLD_PX = 6;

/**
 * The two event families, kept as SEPARATE state machines.
 *
 * Ionic's gesture binds touch events on device (`createPointerEvents` uses
 * touchstart/touchmove/touchend) and falls back to mouse events on desktop, so
 * both have to be guarded. They must not share state: WebKit fires
 * `pointercancel` mid-pan the moment we stop a `pointermove`, and with one
 * shared machine that cancel cleared the block, letting the rest of the pan
 * through to Ionic. Verified on device — that is exactly why an up-then-down
 * drag still dismissed after the first two attempts at this.
 *
 * `pointercancel` is deliberately NOT an end event. It arrives mid-pan as a
 * consequence of the guard doing its job; the authoritative end of a pan is
 * `touchend`/`touchcancel` on device and `pointerup` on desktop, and any fresh
 * interaction starts with a down event, which resets the machine anyway.
 */
const DRAG_EVENT_FAMILIES = [
  { start: 'touchstart', move: 'touchmove', end: ['touchend', 'touchcancel'] },
  { start: 'pointerdown', move: 'pointermove', end: ['pointerup'] },
];

/**
 * `stopPropagation` alone was not enough.
 *
 * It keeps the event from Ionic's gesture, so the SHEET stops moving — but it
 * does nothing to native scrolling, which is a browser behaviour and not a
 * listener. Only `preventDefault` stops that, and it needs a non-passive
 * listener to be allowed to.
 *
 * The hard part is knowing when we are entitled to. Gating on "was the box told
 * its height" was wrong: on the method step the box is told 0.66 of the travel
 * while its content is shorter, so there was nothing to scroll and it scrolled
 * anyway — Ionic's wrapper rubber-banded the whole box, taking the "Payment
 * method" title and the amount off the top of the sheet. Reported from the
 * device, on video.
 *
 * So it is measured instead, per pan: walk the pan's composed path out to the
 * modal and ask whether anything on it can actually scroll further in the
 * direction of travel. Something can — block nothing. Nothing can — the scroll
 * would be pure overscroll, and that is what gets prevented.
 */
const MOVE_LISTENER_OPTIONS = { passive: false, capture: true };
const OBSERVE_LISTENER_OPTIONS = { passive: true, capture: true };

/**
 * How much overflow counts as something a member could actually scroll.
 *
 * Measured on device: Ionic's own `ion-content` scroller inside the sheet
 * reports `scrollHeight` 803 against `clientHeight` 802 — one pixel, from
 * rounding, on a sheet whose content plainly fits. Treating that as scrollable
 * hands the pan to the browser, and iOS overscroll is not bounded by the single
 * pixel of real range: it rubber-bands the content up and off the screen. A
 * pixel or two of slack is an artefact, not an affordance.
 */
const MEANINGFUL_OVERFLOW_PX = 6;

/**
 * Whether an upward pan starting on this event has anything real to scroll.
 *
 * composedPath, not a parentElement walk: the scroller is Ionic's `.modal-wrapper`
 * inside the modal's shadow root, and our content is slotted, so it is not a DOM
 * ancestor of ours. The path crosses that boundary; a parent chain does not.
 *
 * An upward finger movement scrolls content up, which means scrollTop INCREASES
 * — so the room that matters is below the current position, not above.
 */
const hasScrollRoomInPath = (event, boundary) => {
  const path = event.composedPath?.() || [];
  for (const node of path) {
    if (node === boundary) break;
    if (!node || node.nodeType !== 1) continue;
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY !== 'auto' && overflowY !== 'scroll') continue;
    const remaining = node.scrollHeight - node.clientHeight - node.scrollTop;
    if (remaining > MEANINGFUL_OVERFLOW_PX) return true;
  }
  return false;
};

/** Y of a touch or a pointer/mouse event, whichever this is. */
const clientYOf = event => {
  if (event.touches?.length) return event.touches[0].clientY;
  if (event.changedTouches?.length) return event.changedTouches[0].clientY;
  return typeof event.clientY === 'number' ? event.clientY : null;
};

export default {
  name: 'PaymentSheet',

  components: {
    DsBottomSheet,
    PaymentMethodStep,
    PaymentProcessingStep,
    PaymentSuccessStep,
    Pin,
  },

  // Non-reactive instance fields: a rAF id in `data` would re-render the sheet
  // every frame while it is being measured.
  created() {
    this.sheetTrackFrame = null;
    this.autoSizeFrame = null;
    this.onViewportResize = null;
    this.onPresent = null;
    this.onBreakpointChange = null;
    this.dragGuard = null;
  },

  data() {
    return {
      // Measured distance from the top of the sheet's content box to the bottom
      // of the viewport. null until the sheet has been presented and measured,
      // during which the Tailwind fallback height applies. See the template.
      sheetVisibleHeight: null,

      // The sheet's one open stop, and the array Ionic is given.
      //
      // Two entries, only ever `[0, sheetStop]`: 0 so a downward swipe still
      // dismisses, and exactly one open stop so an UPWARD drag has nothing to
      // snap to and springs back. It used to be a ladder of five, and Ionic's
      // gesture snaps to whichever entry is closest on release — which is how
      // the result step at 0.75 could be dragged up to 0.95.
      //
      // MUTATED IN PLACE, never replaced. Ionic hands this array to
      // `createSheetGesture` when the sheet is presented and the gesture keeps
      // that reference for the life of the presentation, so a new array would
      // leave the drag snapping to stops that are no longer on offer. Keep it
      // sorted ascending: the gesture reads its first and last entries as the
      // min and max.
      sheetStops: [0, SHORT_METHOD_STOP],
      sheetStop: SHORT_METHOD_STOP,

      // Set when a content-sized step wanted more room than MAX_SHEET_STOP. The
      // box then goes back to a fixed height so the content scrolls rather than
      // running off the bottom.
      autoSizeOverflowing: false,
      pinMode: 'verify',
      pinLockedUntil: null,
      accountsLoading: true,
      // Which payment the PIN is gating, so a correct PIN resumes the action the
      // member actually chose. 'transfer' = slide-to-pay via the bank rail,
      // 'checkout' = the card/PayNow tile.
      pendingAction: 'transfer',
    };
  },

  computed: {
    ...mapState('payment', ['request', 'selectedMethod', 'isProcessing', 'result']),
    ...mapGetters('payment', ['requiresPin', 'linkedBankAccounts']),

    // Read-only on purpose. The sheet reports a dismissal through
    // @update:modelValue, so a writable v-model would run handleDismiss twice for
    // one gesture.
    isOpen() {
      return this.$store.state.payment.isOpen;
    },

    currentStep() {
      return this.$store.state.payment.currentStep;
    },

    // Bank accounts are hidden entirely when the request does not allow that
    // rail, rather than shown disabled: a dead tile on a payment screen reads
    // as a bug, and the empty state already leads with Card or PayNow. See
    // `allowBankRail` in the payment store for why purchases opt out today.
    offeredBankAccounts() {
      return this.bankRailAllowed ? this.linkedBankAccounts : [];
    },

    // One read of the field, used by both the method step's copy and the list
    // above. It was evaluated inline in two places, which is one place too many
    // for a predicate that decides whether a money rail is on offer.
    bankRailAllowed() {
      return this.request?.allowBankRail !== false;
    },

    offeredAccountCount() {
      return this.offeredBankAccounts.length;
    },

    userEmail() {
      return this.$store.state.user?.profile?.email || this.$store.state.user?.email || '';
    },

    successStatus() {
      if (this.result?.status === PaymentStatus.SUCCESS) return PaymentStatus.SUCCESS;
      if (this.result?.status === PaymentStatus.PENDING) return PaymentStatus.PENDING;
      return PaymentStatus.FAILED;
    },

    canDismiss() {
      return !this.isProcessing && this.currentStep !== 'pin';
    },

    // Inline styles win over the class-based fallback height, so the measured
    // value takes over as soon as there is one.
    sheetBoxStyle() {
      return {
        '--sheet-bp': this.sheetStop,
        paddingBottom: SHEET_FOOT_PADDING,
        ...(this.usesExplicitHeight && this.sheetVisibleHeight
          ? { height: `${this.sheetVisibleHeight}px` }
          : {}),
      };
    },

    /**
     * Whether the box is told how tall to be, or takes its natural height.
     *
     * A content-sized step needs the box free so there is a natural height to
     * measure, and once the sheet has been sized to it the box already ends
     * exactly where the sheet does. Pinning a height there would defeat the
     * measurement it depends on. Everything else — the declared-height steps,
     * and a content-sized step whose content does not fit — gets the measured
     * height, which is what makes its content scroll instead of overflow.
     */
    usesExplicitHeight() {
      return !this.sheetAutoSizes || this.autoSizeOverflowing;
    },

    sheetAutoSizes() {
      return AUTO_SIZED_STEPS.includes(this.currentStep);
    },

    /**
     * Height for the steps that are not content-sized. The method step is full
     * height to seat the accounts list and the slide-to-pay footer; with no
     * account on offer that view is a single card tile, and 0.95 left most of
     * the screen empty.
     *
     * Resolved once per open, at the single sync point in loadAccounts' finally.
     * Watching the count instead queued a second setCurrentBreakpoint on top of
     * that one, and Ionic drops a resize requested while another is still
     * transitioning — which left the accounts view stuck at the short height
     * with its slider below the fold.
     */
    declaredStop() {
      return this.offeredAccountCount ? TALL_METHOD_STOP : SHORT_METHOD_STOP;
    },
  },

  watch: {
    isOpen(val) {
      if (val) {
        this.loadAccounts();
        this.trackSheetHeight();
      } else {
        // Next open starts from the fallback rather than the last sheet's
        // height, which may have been a different stop entirely.
        this.stopTrackingSheetHeight();
        this.cancelAutoSize();
        this.removeDragDirectionGuard();
        this.sheetVisibleHeight = null;
        this.autoSizeOverflowing = false;
      }
    },

    currentStep() {
      this.$nextTick(this.syncSheetBreakpoint);
    },
  },

  methods: {
    ...mapActions('payment', [
      'selectMethod',
      'processPayment',
      'closeSheet',
      'fetchLinkedAccounts',
      'payWithHitPay',
    ]),

    async loadAccounts() {
      this.accountsLoading = true;
      try {
        await this.fetchLinkedAccounts();
      } finally {
        this.accountsLoading = false;
        this.syncSheetBreakpoint();
      }
    },

    /**
     * Put the sheet at the height the current step wants.
     *
     * `force` is for present. Ionic keeps its own `currentBreakpoint` across a
     * dismiss/present cycle and `initialBreakpoint` is only read once, at load,
     * so a sheet reopened after a short step presents at that short stop. Our
     * cached `sheetStop` may already equal the target, which would skip the very
     * call that corrects it — hence forcing the call on the way in.
     */
    syncSheetBreakpoint(force = false) {
      this.installDragDirectionGuard();
      if (this.sheetAutoSizes) {
        this.scheduleAutoSize(force);
        return;
      }
      this.applySheetStop(this.declaredStop, { force });
      // The height has to follow the stop, and the stop animates.
      this.trackSheetHeight();
    },

    /**
     * Move the sheet to `stop`, and make it the only open stop on offer.
     *
     * Order matters: Ionic validates `setCurrentBreakpoint` against the live
     * `breakpoints` prop, so the array has to carry the new value before the
     * call. `splice` rather than reassignment because the drag gesture holds a
     * reference to this exact array — see the note on `sheetStops`.
     */
    applySheetStop(stop, { force = false } = {}) {
      // Quantised so a sub-pixel measurement difference is not a new stop, and a
      // no-op is skipped: Ionic drops a resize requested while another is still
      // transitioning.
      //
      // Rounded UP, not to nearest. Rounding down can leave the sheet a
      // fraction of a pixel shorter than the content it was measured from,
      // which makes the content overflow by a pixel — enough to turn the body
      // into a live scroll container that absorbs the pan, so a sheet whose
      // content plainly fits still felt scrollable.
      const next = Math.ceil(clampStop(stop) * 1000) / 1000;
      if (next === this.sheetStop && !force) return;

      this.sheetStop = next;
      this.sheetStops.splice(0, this.sheetStops.length, 0, next);

      const sheet = this.$refs.paymentSheet?.$el;
      if (!sheet?.setCurrentBreakpoint) return;
      sheet.setCurrentBreakpoint(next)?.catch(() => {});
    },

    /**
     * Size a content-sized step to its content.
     *
     * Measures a frame late on purpose. `usesExplicitHeight` is already false
     * for these steps, but the box may still be carrying the previous step's
     * measured height in the DOM. The writes below that release it are
     * reactive, and Vue flushes those on a microtask — which lands before the
     * frame callback runs, so what gets measured is a box genuinely at its
     * natural height rather than the last step's.
     */
    scheduleAutoSize(force = false) {
      this.cancelAutoSize();
      this.autoSizeOverflowing = false;
      this.sheetVisibleHeight = null;

      this.autoSizeFrame = requestAnimationFrame(() => {
        this.autoSizeFrame = null;
        const fit = this.measureContentStop();
        if (!fit) return;

        this.autoSizeOverflowing = fit.overflowing;
        this.applySheetStop(fit.stop, { force });
        // Only meaningful when the content overflowed and the box is going back
        // to a fixed height; harmless otherwise.
        if (fit.overflowing) this.trackSheetHeight();
      });
    },

    cancelAutoSize() {
      if (this.autoSizeFrame) {
        cancelAnimationFrame(this.autoSizeFrame);
        this.autoSizeFrame = null;
      }
    },

    /**
     * The content box's rect, or null when there is nothing trustworthy to
     * measure. Both measurements below start here, and both are wrong in the
     * same two ways without it.
     *
     * An ion-modal that has not been presented is `display: none`, so its
     * subtree has no layout and EVERY rect value is 0. `viewport - 0` is the
     * whole screen — a plausible-looking number that is the original overshoot
     * bug with a bigger overshoot, so it has to be rejected before the
     * arithmetic, not after. Caught by measuring a real sheet in the
     * /payment-states harness, which latched 874px.
     *
     * And a presented sheet always has its top below the viewport top: the
     * tallest stop on offer is 0.95. Zero or negative means it is
     * mid-transition, mid-dismiss, or not on screen.
     */
    readSheetBoxRect() {
      const box = this.$refs.sheetBox;
      if (!box) return null;

      const rect = box.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return null;
      if (rect.top <= 0) return null;
      return rect;
    },

    /**
     * Work out the stop at which the sheet is exactly as tall as its content.
     *
     * A stop is a fraction of the sheet's TRAVEL, not of the viewport. Ionic
     * insets its own wrapper, so `stop x viewport` is simply the wrong unit —
     * that mistake is what made the box overshoot by 46pt and clip the Close
     * button off the screen. The wrapper's height is the real denominator, and
     * the chrome it puts above our box (handle, wrapper padding) is measured
     * from where the box actually is rather than assumed:
     *
     *   box top = viewport - stop x travel + chrome
     *
     * Returns null when there is nothing trustworthy to measure.
     */
    measureContentStop() {
      const rect = this.readSheetBoxRect();
      if (!rect) return null;

      const frame = this.sheetFrame();
      if (!frame) return null;

      // Sheet chrome above our box: the handle, and whatever padding the
      // wrapper puts around its content.
      //
      // Taken as the gap between two rects that share the wrapper's transform,
      // so it cancels out and this is a pure layout value. Deriving it from the
      // current stop instead read as ~144pt too large on a sheet measured while
      // it was still animating in, which sized the processing step TALLER than
      // the result step it has a third the content of.
      const chrome = rect.top - frame.top;
      // The box is at its natural height right now, so this IS the content.
      const wanted = (rect.height + chrome) / frame.travel;
      if (!Number.isFinite(wanted) || wanted <= 0) return null;

      return { stop: clampStop(wanted), overflowing: wanted > MAX_SHEET_STOP };
    },

    /**
     * The sheet's wrapper: its full travel in px — the denominator every stop is
     * a fraction of, and NOT the viewport, since Ionic insets it — and its
     * current top, for measuring the chrome above our box.
     *
     * Ionic keeps the wrapper inside its own (open) shadow root. Returns null
     * when it cannot be read, which leaves a content-sized step at whatever
     * stop it already had rather than guessing a new one.
     */
    sheetFrame() {
      const sheet = this.$refs.paymentSheet?.$el;
      const wrapper = sheet?.shadowRoot?.querySelector('.modal-wrapper');
      const travel = wrapper?.clientHeight || 0;
      if (!travel) return null;
      return { travel, top: wrapper.getBoundingClientRect().top };
    },

    /**
     * Stop the sheet being dragged UP, while leaving swipe-down-to-dismiss.
     *
     * Ionic has no way to ask for this, and two obvious routes are dead ends:
     *
     *  - `breakpoints` only decides where a RELEASED drag snaps to. `onMove`
     *    clamps the live offset to 0.0001 regardless, so the sheet rubber-bands
     *    up to nearly full screen however few stops are on offer.
     *  - A competing higher-priority gesture cannot be direction-aware:
     *    `canStart` runs on pointerdown, where `deltaY` is reset to 0, and the
     *    pan recogniser tests only the AXIS, not the sign. Such a gesture would
     *    capture downward pans too, taking dismissal with it.
     *  - And Ionic's own gesture object cannot be switched off, because
     *    `el.gesture` is not reachable: the app loads Ionic's lazy build, where
     *    the component instance lives off the element. (Verified on device:
     *    `sheet.gesture` is undefined.)
     *
     * What does work is not letting the event reach Ionic at all. Its sheet
     * gesture listens for `touchmove` on `.modal-wrapper` and, on desktop, for
     * `mousemove` on the document — both in the bubble phase. A CAPTURE-phase
     * listener on the document runs before either, so calling
     * `stopPropagation()` there means Ionic never sees an upward move and never
     * moves the sheet. Downward moves are passed straight through, so swiping
     * down still dismisses.
     *
     * Capture on the document specifically, not on the modal: registering on
     * the same element as Ionic buys nothing, because at a given target
     * listeners run in REGISTRATION order and the capture flag does not reorder
     * them — and Ionic registers first.
     *
     * The listeners stay passive and never call preventDefault, so nothing here
     * can swallow a scroll; `stopPropagation` is unaffected by passive.
     */
    installDragDirectionGuard() {
      this.removeDragDirectionGuard();

      const sheet = this.$refs.paymentSheet?.$el;
      if (!sheet) return;

      // Only pans that started inside this sheet. composedPath, not
      // `contains`: our content is slotted, so it is not a DOM descendant of
      // the shadow wrapper and `contains` would be false for every touch.
      const insideSheet = event => event.composedPath?.().includes(sheet);

      this.dragGuard = DRAG_EVENT_FAMILIES.map(family => {
        let startY = null;
        let blocking = false;
        // Measured once per pan: cheap enough at pan start, and a layout read
        // on every move would not be.
        let scrollRoom = false;

        const onDown = event => {
          blocking = false;
          const inside = insideSheet(event);
          startY = inside ? clientYOf(event) : null;
          scrollRoom = inside ? hasScrollRoomInPath(event, sheet) : false;
        };

        const onMove = event => {
          if (startY === null) return;
          if (!blocking) {
            const currentY = clientYOf(event);
            if (currentY === null) return;
            const deltaY = currentY - startY;
            // Below the threshold a pan has no direction yet. Ionic needs 10px
            // before it can capture, so letting these few through is safe.
            if (Math.abs(deltaY) < DRAG_DIRECTION_THRESHOLD_PX) return;
            // Downward is a dismissal; leave it entirely alone, and stop
            // watching this pan. A pan that starts downward and reverses keeps
            // its drag, which is a lift-and-retry away and not worth the state.
            if (deltaY >= 0) {
              startY = null;
              return;
            }
            blocking = true;
          }
          // Keeps it from Ionic's gesture: the sheet does not move.
          event.stopPropagation();
          // Keeps it from the browser: the content does not slide up. Skipped
          // entirely when something on the path can genuinely scroll, so this
          // can never block a real scroll.
          if (!scrollRoom && event.cancelable) event.preventDefault();
        };

        const onEnd = () => {
          startY = null;
          blocking = false;
        };

        document.addEventListener(family.start, onDown, OBSERVE_LISTENER_OPTIONS);
        document.addEventListener(family.move, onMove, MOVE_LISTENER_OPTIONS);
        family.end.forEach(name =>
          document.addEventListener(name, onEnd, OBSERVE_LISTENER_OPTIONS),
        );

        return { family, onDown, onMove, onEnd };
      });
    },

    removeDragDirectionGuard() {
      if (!this.dragGuard) return;

      this.dragGuard.forEach(({ family, onDown, onMove, onEnd }) => {
        document.removeEventListener(family.start, onDown, OBSERVE_LISTENER_OPTIONS);
        document.removeEventListener(family.move, onMove, MOVE_LISTENER_OPTIONS);
        family.end.forEach(name =>
          document.removeEventListener(name, onEnd, OBSERVE_LISTENER_OPTIONS),
        );
      });
      this.dragGuard = null;
    },

    /**
     * Measure the sheet's visible height: the gap between the top of its content
     * box and the bottom of the viewport.
     *
     * No feedback loop — the box's top is set by Ionic's (fixed-height,
     * transformed) wrapper and does not depend on the box's own height, so
     * writing the height back cannot move what we just measured.
     */
    measureSheetHeight() {
      const rect = this.readSheetBoxRect();
      if (!rect) return;

      const viewport = window.visualViewport?.height || window.innerHeight;
      const height = Math.round(viewport - rect.top);
      if (!Number.isFinite(height) || height <= 0) return;
      this.sheetVisibleHeight = height;
    },

    /**
     * Re-measure for the length of a breakpoint transition, so the foot tracks
     * the animation instead of snapping once it ends. Also the only thing that
     * produces the FIRST measurement, which is why it runs on open as well as
     * on every step change.
     */
    trackSheetHeight() {
      this.stopTrackingSheetHeight();
      const deadline = Date.now() + SHEET_TRANSITION_TRACK_MS;
      const step = () => {
        this.measureSheetHeight();
        if (Date.now() >= deadline) {
          this.sheetTrackFrame = null;
          return;
        }
        this.sheetTrackFrame = requestAnimationFrame(step);
      };
      step();
    },

    stopTrackingSheetHeight() {
      if (this.sheetTrackFrame) {
        cancelAnimationFrame(this.sheetTrackFrame);
        this.sheetTrackFrame = null;
      }
    },

    handleMethodSelect(method) {
      this.selectMethod(method);
    },

    // One hosted-checkout path on every platform — HitPay has no native SDK, so
    // there is no native/web branch and no mock fallback. The transaction was
    // already opened server-side by the caller; the sheet just presents it.
    //
    // PIN-gated on the same threshold as the bank rail. It previously went
    // straight to checkout, so a S$1,200 bank transfer asked for a PIN while a
    // S$12,000 card payment did not — the gate had a side door.
    handleCheckoutTile() {
      if (this.requiresPin) {
        this.pendingAction = 'checkout';
        this.pinMode = 'verify';
        this.$store.commit('payment/SET_STEP', 'pin');
        return undefined;
      }
      return this.payWithHitPay();
    },

    // Slide-to-pay completed → PIN-gate large payments, else pay now.
    handleConfirm() {
      if (this.requiresPin) {
        this.pendingAction = 'transfer';
        this.pinMode = 'verify';
        this.$store.commit('payment/SET_STEP', 'pin');
      } else {
        this.processPayment();
      }
    },

    goBackToMethod() {
      this.$store.commit('payment/SET_STEP', 'method');
      this.$nextTick(() => this.$refs.methodStep?.resetSlider());
    },

    async handlePinVerify(pin) {
      try {
        await this.$store.dispatch('myGiving/verifyPin', { pin });
        const pinState = this.$store.state.myGiving.pinStatus;

        if (pinState === 'pin_matched') {
          if (this.pendingAction === 'checkout') {
            this.payWithHitPay();
          } else {
            this.processPayment();
          }
        } else if (pinState === 'pin_locked') {
          this.pinMode = 'locked';
          this.pinLockedUntil = this.$store.state.myGiving.pinLockedUntil;
        } else {
          const attemptsLeft = this.$store.state.myGiving.pinAttemptsLeft;
          this.$refs.pinOverlay?.showError?.(
            `Incorrect PIN. ${attemptsLeft} attempt${attemptsLeft !== 1 ? 's' : ''} remaining.`,
          );
        }
      } catch {
        // Network/timeout error — clear the overlay's loading state so the user
        // can retry or back out (PinOverlay stays disabled until showError runs).
        this.$refs.pinOverlay?.showError?.('Something went wrong. Please try again.');
      }
    },

    handleForgotPin() {
      this.closeSheet();
      this.$router.push({ path: '/my-giving/pin/forgot-pin' });
    },

    // The sheet has already gone from the screen by the time this runs, so the
    // only question is whether the store agrees. It always must.
    //
    // This used to be gated on canDismiss, which reads as "refuse the dismissal"
    // but cannot be — the gesture already happened. All the guard did was leave
    // isOpen true with nothing on screen and the caller's promise unresolved, so
    // the buy card spun forever and no later attempt could present the sheet.
    // Dismissal mid-processing is already an expected path: payWithHitPay checks
    // `!state.isOpen` after the browser closes and after each poll, and bails out
    // without touching the result.
    handleDismiss(value) {
      if (!value) this.closeSheet();
    },
  },

  // Vue hyphenates custom-element event names, so `@ionBreakpointDidChange` on
  // the component never fires. Bind on the element.
  mounted() {
    this.onViewportResize = () => this.measureSheetHeight();
    window.addEventListener('resize', this.onViewportResize);
    window.visualViewport?.addEventListener('resize', this.onViewportResize);

    const sheet = this.$refs.paymentSheet?.$el;
    if (sheet) {
      // A stop change only re-measures the height. It must NOT re-run
      // auto-sizing: auto-sizing sets the stop, so that would be a loop.
      this.onBreakpointChange = () => this.trackSheetHeight();
      // Present is the one place auto-sizing has to be kicked off from, for a
      // sheet that opens straight onto a content-sized step.
      this.onPresent = () => {
        this.installDragDirectionGuard();
        this.syncSheetBreakpoint(true);
      };
      sheet.addEventListener('ionBreakpointDidChange', this.onBreakpointChange);
      sheet.addEventListener('ionModalDidPresent', this.onPresent);
    }
  },

  beforeUnmount() {
    this.stopTrackingSheetHeight();
    this.cancelAutoSize();
    this.removeDragDirectionGuard();
    window.removeEventListener('resize', this.onViewportResize);
    window.visualViewport?.removeEventListener('resize', this.onViewportResize);

    const sheet = this.$refs.paymentSheet?.$el;
    if (sheet) {
      sheet.removeEventListener('ionBreakpointDidChange', this.onBreakpointChange);
      sheet.removeEventListener('ionModalDidPresent', this.onPresent);
    }
  },
};
</script>

<!-- Vanilla CSS, deliberately, for the one thing Tailwind cannot express: a
     custom property on a component we do not render. `ion-content` is inside
     the design-system sheet, and `--overflow` is Ionic's own hook for its
     scroller (`.scroll-y { overflow-y: var(--overflow) }`).

     Turning it off leaves the sheet with exactly ONE scroll container. It had
     two, nested: each step's own scrollable body inside Ionic's ion-content
     scroller, which is what the double-scroll feel came from. An on-device
     readout of a pan's element path showed both — ours at 770/770 and Ionic's
     at 803/802, the latter a pixel of rounding on content that fits.

     Ours is the one to keep: the steps pin a footer (slide-to-pay, Done) below
     a scrollable body, which needs the box to be a flex column with only the
     body scrolling. If ion-content were the scroller, the footer would scroll
     away with the content.

     Note this is NOT about the bounce. Ionic already ships
     `overscroll-behavior-y: contain` on `.scroll-y`; a rule adding that here
     was redundant and has been removed.

     Unscoped because the target is inside a component we do not own; keyed off
     the design-system class so it cannot leak to other sheets. -->
<style>
.ds-bottom-sheet ion-content {
  --overflow: hidden;
}
</style>
